/**
 * Email Service for Konzo Marketplace
 * Handles all email communications with customers and artists
 */

// Firebase utilities will be used in future implementations
// import { marketplaceApp } from '../firebase.marketplace';

// Email templates for different order statuses and notifications
const emailTemplates = {
  digitalDownload: {
    subject: 'Your Music Download is Ready - Konzo Marketplace',
    template: (order) => `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Your Music Download is Ready</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background-color: #4a0082; padding: 20px; text-align: center; }
          .header h1 { color: white; margin: 0; }
          .content { padding: 20px; background-color: #fff; }
          .footer { background-color: #f5f5f5; padding: 15px; text-align: center; font-size: 12px; color: #666; }
          .button { display: inline-block; padding: 12px 24px; background-color: #4a0082; color: white; text-decoration: none; border-radius: 4px; font-weight: bold; }
          .music-item { margin-bottom: 25px; padding: 15px; border: 1px solid #e0e0e0; border-radius: 8px; background-color: #f9f9f9; }
          .music-title { margin: 0 0 10px 0; color: #4a0082; }
          .music-info { margin: 0 0 15px 0; }
          .download-section { margin-top: 15px; }
          .divider { height: 1px; background-color: #e0e0e0; margin: 20px 0; }
          .order-info { background-color: #f5f9ff; padding: 15px; border-radius: 8px; margin: 20px 0; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Your Music is Ready!</h1>
          </div>
          <div class="content">
            <h2>Thank you for your purchase!</h2>
            <p>We're excited to deliver your music from Konzo Marketplace. Your support helps Ugandan artists continue creating amazing music.</p>
            
            <div class="order-info">
              <p><strong>Order ID:</strong> ${order.orderId || order.id || 'N/A'}</p>
              <p><strong>Purchase Date:</strong> ${new Date().toLocaleDateString()}</p>
              <p><strong>Payment Method:</strong> Mobile Money</p>
            </div>
            
            <h3>Your Digital Downloads</h3>
            ${order.items.filter(item => item.isDigitalDownload && item.audioFileUrl).map(item => `
              <div class="music-item">
                <h4 class="music-title">${item.title}</h4>
                <p class="music-info"><strong>Artist:</strong> ${item.artist || 'Konzo Artist'}</p>
                ${item.imageUrl ? `<img src="${item.imageUrl}" alt="${item.title}" style="max-width: 100px; height: auto; margin-bottom: 10px;">` : ''}
                <div class="download-section">
                  <a href="${item.audioFileUrl}" class="button">Download MP3</a>
                </div>
              </div>
            `).join('')}
            
            <div class="divider"></div>
            
            <p>You can also access your purchases at any time by visiting your <a href="${process.env.REACT_APP_BASE_URL || window.location.origin}/marketplace/orders">Order History</a> in your account.</p>
            
            <p><strong>Need help?</strong> If you have any questions or issues with your downloads, please contact our support team at <a href="mailto:wilbert@kihembo.com">wilbert@kihembo.com</a>.</p>
          </div>
          <div class="footer">
            <p>&copy; ${new Date().getFullYear()} Konzo Marketplace. All rights reserved.</p>
            <p>This email was sent to you because you made a purchase on Konzo Marketplace.</p>
          </div>
        </div>
      </body>
      </html>
    `
  },
  paymentSuccess: {
    subject: 'Payment Confirmed - Your Konzo Marketplace Order',
    template: (order) => `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Payment Confirmed</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background-color: #4a0082; padding: 20px; text-align: center; }
          .header h1 { color: white; margin: 0; }
          .content { padding: 20px; background-color: #fff; }
          .footer { background-color: #f5f5f5; padding: 15px; text-align: center; font-size: 12px; color: #666; }
          .button { display: inline-block; padding: 12px 24px; background-color: #4a0082; color: white; text-decoration: none; border-radius: 4px; font-weight: bold; }
          .success-icon { font-size: 48px; color: #28a745; text-align: center; margin: 20px 0; }
          .order-details { background-color: #f9f9f9; padding: 15px; border-radius: 8px; margin: 20px 0; }
          .item-row { padding: 10px 0; border-bottom: 1px solid #eee; }
          .item-row:last-child { border-bottom: none; }
          .total-row { font-weight: bold; padding-top: 15px; border-top: 2px solid #ddd; margin-top: 15px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Payment Confirmed!</h1>
          </div>
          <div class="content">
            <div class="success-icon">✓</div>
            <h2>Thank you for your purchase!</h2>
            <p>Your payment has been successfully processed and your order is confirmed.</p>
            
            <div class="order-details">
              <p><strong>Order ID:</strong> ${order.orderId || order.id || 'N/A'}</p>
              <p><strong>Transaction ID:</strong> ${order.transactionId || 'Processing'}</p>
              <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
              <p><strong>Payment Method:</strong> Mobile Money (${order.mobileProvider || 'MTN/Airtel'})</p>
              
              <h3>Order Summary</h3>
              ${order.items.map(item => `
                <div class="item-row">
                  <strong>${item.title}</strong> ${item.quantity > 1 ? `x ${item.quantity}` : ''}<br>
                  ${item.isDigitalDownload ? '<span style="color: #4a0082;">Digital Download</span>' : ''}
                  <div style="float: right;">${new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX' }).format(item.price * (item.quantity || 1))}</div>
                </div>
              `).join('')}
              
              <div class="total-row">
                Total: ${new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX' }).format(order.total)}
              </div>
            </div>
            
            ${order.items.some(item => item.isDigitalDownload) ? `
              <p><strong>Digital Downloads:</strong> A separate email with your download links will be sent shortly.</p>
            ` : ''}
            
            <p>You can view your order details and track status at any time by visiting your <a href="${process.env.REACT_APP_BASE_URL || window.location.origin}/marketplace/orders">Order History</a>.</p>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${process.env.REACT_APP_BASE_URL || window.location.origin}/marketplace/orders" class="button">View Order Details</a>
            </div>
            
            <p>If you have any questions about your order, please contact our customer support at <a href="mailto:wilbert@kihembo.com">wilbert@kihembo.com</a>.</p>
          </div>
          <div class="footer">
            <p>&copy; ${new Date().getFullYear()} Konzo Marketplace. All rights reserved.</p>
            <p>This email was sent to confirm your recent purchase.</p>
          </div>
        </div>
      </body>
      </html>
    `
  },
  processing: {
    subject: 'Order Update - Your Order is Being Processed',
    template: (order) => `
      <h2>Your order is being processed!</h2>
      <p>We're currently preparing your order #${order.orderId} for shipment.</p>
      <p>We'll notify you as soon as it's on its way.</p>
      <h3>Order Details:</h3>
      <ul>
        ${order.items.map(item => `
          <li>${item.title} x ${item.quantity}</li>
        `).join('')}
      </ul>
    `
  },
  shipped: {
    subject: 'Order Shipped - Your Order is on the Way',
    template: (order) => `
      <h2>Your order has been shipped!</h2>
      <p>Great news! Your order #${order.orderId} is on its way to you.</p>
      ${order.trackingNumber ? `
        <p>Tracking Number: ${order.trackingNumber}</p>
        <p>You can track your package using this number on our website.</p>
      ` : ''}
      <h3>Shipped Items:</h3>
      <ul>
        ${order.items.map(item => `
          <li>${item.title} x ${item.quantity}</li>
        `).join('')}
      </ul>
    `
  },
  completed: {
    subject: 'Order Delivered - Thank You for Shopping with Us',
    template: (order) => `
      <h2>Your order has been delivered!</h2>
      <p>We hope you're enjoying your items from order #${order.orderId}.</p>
      <p>If you have a moment, we'd love to hear your feedback about the products.</p>
      <h3>Order Details:</h3>
      <ul>
        ${order.items.map(item => `
          <li>${item.title} x ${item.quantity}</li>
        `).join('')}
      </ul>
      <p>Thank you for choosing us!</p>
    `
  },
  cancelled: {
    subject: 'Order Cancelled - Your Konzo Marketplace Order',
    template: (order) => `
      <h2>Your order has been cancelled</h2>
      <p>We're sorry to inform you that order #${order.orderId} has been cancelled.</p>
      <p>If you didn't request this cancellation or have any questions, please contact our support team.</p>
      <h3>Cancelled Items:</h3>
      <ul>
        ${order.items.map(item => `
          <li>${item.title} x ${item.quantity}</li>
        `).join('')}
      </ul>
    `
  }
};

/**
 * Sends an email notification about order status changes
 * @param {string} email - Recipient email address
 * @param {Object} orderData - Order data including status and items
 * @returns {Promise} - Resolution of the email sending attempt
 */
export const sendOrderStatusEmail = async (email, orderData) => {
  if (!email || !orderData) {
    console.error('Missing required parameters for sending order status email');
    return Promise.reject(new Error('Missing email or order data'));
  }

  // Determine which template to use based on order status
  let status = orderData.status || 'pending';
  
  // For payment confirmations, use the new payment success template
  if (status === 'completed' || status === 'processing' || orderData.paymentStatus === 'completed') {
    status = 'paymentSuccess';
  }
  
  const template = emailTemplates[status];
  
  if (!template) {
    console.error(`No email template found for status: ${status}`);
    // Fallback to payment success template if specific status template not found
    status = 'paymentSuccess';
  }

  // Prepare email data
  const emailData = {
    to: email,
    subject: emailTemplates[status].subject.replace('{orderId}', orderData.orderId || orderData.id || 'N/A'),
    html: emailTemplates[status].template(orderData)
  };

  try {
    // In a real implementation, you would call your email service here
    console.log('Sending order status email:', emailData.subject);
    
    // Track email sending in analytics if available
    try {
      if (window.gtag) {
        window.gtag('event', 'email_sent', {
          'event_category': 'email',
          'event_label': status,
          'value': 1
        });
      }
    } catch (analyticsError) {
      console.warn('Analytics error:', analyticsError);
    }
    
    // Call the sendEmail function
    return sendEmail(emailData);
  } catch (error) {
    console.error('Error sending order status email:', error);
    // Still return a resolved promise to prevent blocking the checkout flow
    return Promise.resolve({
      success: false,
      error: error.message
    });
  }
};

/**
 * Sends an email with MP3 download links for digital music purchases
 * @param {string} email - Recipient email address
 * @param {Object} orderData - Order data including digital download items
 * @returns {Promise} - Resolution of the email sending attempt
 */
export const sendDigitalDownloadEmail = async (email, orderData) => {
  if (!email || !orderData || !orderData.items) {
    console.error('Missing required parameters for sending digital download email');
    return Promise.resolve({ success: false, error: 'Missing email or order data' });
  }

  // Filter for digital items only
  const digitalItems = orderData.items.filter(item => 
    item.isDigitalDownload && item.audioFileUrl
  );

  if (digitalItems.length === 0) {
    console.warn('No digital items found in order, skipping download email');
    return Promise.resolve({ success: true, skipped: true, reason: 'No digital items' });
  }

  // Add download expiration date (7 days from now)
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + 7);
  
  // Add tracking parameters to download URLs if needed
  const enhancedItems = digitalItems.map(item => ({
    ...item,
    // Add tracking parameters to download URLs
    audioFileUrl: item.audioFileUrl.includes('?') 
      ? `${item.audioFileUrl}&source=email&orderId=${orderData.orderId || orderData.id || 'direct'}` 
      : `${item.audioFileUrl}?source=email&orderId=${orderData.orderId || orderData.id || 'direct'}`
  }));

  // Prepare email data using the digital download template
  const emailData = {
    to: email,
    subject: emailTemplates.digitalDownload.subject,
    html: emailTemplates.digitalDownload.template({
      ...orderData,
      items: enhancedItems,
      expirationDate: expirationDate.toLocaleDateString()
    })
  };

  try {
    // Track digital download email in analytics if available
    try {
      if (window.gtag) {
        window.gtag('event', 'digital_download_email_sent', {
          'event_category': 'email',
          'event_label': 'digital_product',
          'value': digitalItems.length
        });
      }
    } catch (analyticsError) {
      console.warn('Analytics error:', analyticsError);
    }
    
    console.log('Sending digital download email for', digitalItems.length, 'items');
    
    // Send the email
    return sendEmail(emailData);
  } catch (error) {
    console.error('Error sending digital download email:', error);
    // Still return a resolved promise to prevent blocking the checkout flow
    return Promise.resolve({
      success: false,
      error: error.message
    });
  }
};

/**
 * Sends a purchase confirmation email for digital products
 * @param {string} email - Buyer's email address
 * @param {Object} purchaseData - Purchase details including download links
 * @returns {Promise} - Resolution of the email sending attempt
 */
export const sendDigitalPurchaseEmail = async (email, purchaseData) => {
  try {
    const { orderId, items, downloadLinks } = purchaseData;
    // Create email data object (would be sent to a real email service in production)
    const emailData = {
      to: email,
      subject: 'Your Digital Purchase is Ready!',
      html: `
        <h2>Your digital items are ready for download!</h2>
        <p>Thank you for your purchase (Order #${orderId}). Your digital items are ready to download.</p>
        <h3>Your Items:</h3>
        <ul>
          ${items.map((item, index) => `
            <li>
              ${item.title}
              <br>
              Download Link: <a href="${downloadLinks[index]}">Click here to download</a>
              <br>
              <small>Link expires in 24 hours</small>
            </li>
          `).join('')}
        </ul>
        <p>Note: Download links will expire in 24 hours for security purposes.</p>
        <p>If you have any issues with the downloads, please contact our support team.</p>
      `
    };

    // Log the email that would be sent (for development/testing)
    console.log('Digital purchase email would be sent:', emailData);
    return { success: true, message: 'Email logged (not actually sent in development)' };
  } catch (error) {
    console.error('Error sending digital purchase email:', error);
    throw error;
  }
};

/**
 * Mock email service for development
 * @param {Object} emailData - Email data including to, subject, and html
 * @returns {Promise} - Resolution of the email sending attempt
 */
const sendEmail = (emailData) => {
  // In production, this would connect to your email service
  console.log('MOCK EMAIL SERVICE:', emailData.subject);
  
  // Log the first 100 characters of the email body for debugging
  const previewText = emailData.html.replace(/<[^>]*>/g, ' ').substring(0, 100) + '...';
  console.log('Email preview:', previewText);
  
  // Simulate network delay for more realistic testing
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ 
        success: true,
        messageId: `mock-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        timestamp: new Date().toISOString()
      });
    }, 500);
  });
};

/**
 * Sends a notification email for upcoming streaming events
 * @param {string} email - Ticket holder's email address
 * @param {Object} eventData - Event details including streaming link
 * @returns {Promise} - Resolution of the email sending attempt
 */
export const sendStreamingEventReminder = async (email, eventData) => {
  try {
    const { eventTitle, startTime, streamingLink, ticketId } = eventData;
    // Create email data object (would be sent to a real email service in production)
    const emailData = {
      to: email,
      subject: `Reminder: ${eventTitle} Starts Soon!`,
      html: `
        <h2>Your streaming event is starting soon!</h2>
        <p>Get ready for ${eventTitle}!</p>
        <h3>Event Details:</h3>
        <ul>
          <li>Event: ${eventTitle}</li>
          <li>Start Time: ${new Date(startTime).toLocaleString()}</li>
          <li>Ticket ID: ${ticketId}</li>
        </ul>
        <p><a href="${streamingLink}">Click here to join the stream</a></p>
        <p>We recommend joining a few minutes early to ensure everything is working properly.</p>
        <p>Need help? Contact our support team for assistance.</p>
      `
    };

    // Log the email that would be sent (for development/testing)
    console.log('Streaming event reminder email would be sent:', emailData);
    return { success: true, message: 'Email logged (not actually sent in development)' };
  } catch (error) {
    console.error('Error sending streaming event reminder:', error);
    throw error;
  }
};
